import classnames from "classnames";
import HoverClickPopup from "@components/ui/general/HoverClickPopup";
import { getModifierBreakdown } from "@utils/statCalculations";
import { Zap } from "lucide-react";
import EffectIcon from "@components/ui/icons/EffectIcon";
import UnitIcon from "@gameobjects/UnitIcon";
import useGameStore from "@store/useGameStore";
import { getSourceIcon } from "@utils/statIcons";
import "./APDisplay.css";

/**
 * Muestra esferas de Puntos de Acción (AP), ya sea grandes o mini.
 *
 * @param {number} total  - Cantidad total de esferas.
 * @param {number} filled - Cuántas están rellenas.
 * @param {'normal' | 'small'} size - Tamaño de las esferas (por defecto: 'normal').
 * @param {object} unit - Unit object to get AP modifiers from (optional, for detailed tooltip)
 */
export default function APDisplay({
  baseTotal = 0,  // Base max AP (from template)
  total = 0,      // Effective max AP (with modifiers)
  filled = 0,     // Current available AP
  size = "normal",
  unit = null,
  placement = "top",  // Tooltip placement: 'top' | 'bottom' | 'left' | 'right'
  showTooltip = true,  // Whether to show tooltip on hover
  showLabel = false   // Whether to show "AP" label before spheres
}) {
  // Get AP modifiers if unit is provided
  const apBreakdown = unit ? getModifierBreakdown(unit, 'ap') : null;
  const maxApBreakdown = unit ? getModifierBreakdown(unit, 'max_ap') : null;
  
  // Calculate modifier totals
  const maxApModifierTotal = maxApBreakdown ? 
    maxApBreakdown.modifiers.reduce((sum, mod) => sum + (mod.value || 0), 0) : 0;
  
  // No fallbacks - fail loudly if baseTotal is not provided when needed
  if (!baseTotal && baseTotal !== 0) {
    console.error('[APDisplay] baseTotal is required but not provided');
    throw new Error('APDisplay requires baseTotal prop');
  }
  const displayTotal = baseTotal;
  
  // Build the AP display
  const apDisplay = (
    <div
      className={classnames("ap-display", {
        "ap-display-small": size === "small",
        "ap-display-with-label": showLabel,
      })}
    >
      {showLabel && <span className="ap-label">AP</span>}
      {Array.from({ length: displayTotal }).map((_, i) => {
        const isFilled = i < filled;
        const baseClass = size === "small" ? "ap-cost-mini-sphere" : "ap-sphere";
        
        // Determine visual state for each bullet
        let bulletClass = '';
        
        // If we have less effective AP than base, show disabled bullets
        if (total < displayTotal && i >= total) {
          // These AP points are disabled/lost - show as red hollow
          return (
            <div
              key={i}
              className={classnames(baseClass, 'hollow', 'disabled')}
            />
          );
        }
        
        // For bonus AP (when effective > base), this shouldn't happen with our current logic
        // since we use displayTotal = baseTotal, but kept for safety
        if (total > displayTotal && i >= displayTotal) {
          bulletClass = 'buffed';
        }

        return (
          <div
            key={i}
            className={classnames(baseClass, {
              filled: isFilled,
              hollow: !isFilled,
              [bulletClass]: bulletClass
            })}
          />
        );
      })}
    </div>
  );

  // If tooltip disabled, return just the display
  if (!showTooltip) {
    return apDisplay;
  }

  // If no unit provided, show simple tooltip with just description
  if (!unit || !apBreakdown) {
    const simplePopupContent = (
      <>
        <div className="click-popup-header">
          <div className="click-popup-title-group">
            <span className="click-popup-icon">
              <Zap stroke="#8b5cf6" className="icon-fixed" />
            </span>
            <span className="click-popup-title">Action Points</span>
          </div>
        </div>
        <div className="click-popup-description">
          Points used to perform actions during combat
        </div>
      </>
    );

    return (
      <HoverClickPopup
        trigger={apDisplay}
        placement={placement}
      >
        {simplePopupContent}
      </HoverClickPopup>
    );
  }
  
  // Build popup content for AP modifiers
  const apPopupContent = (
    <>
      {/* Header */}
      <div className="click-popup-header">
        <div className="click-popup-title-group">
          <span className="click-popup-icon">
            <Zap stroke="#8b5cf6" className="icon-fixed" />
          </span>
          <span className="click-popup-title">Action Points</span>
        </div>
      </div>

      {/* Description */}
      <div className="click-popup-description">
        Current AP and effects affecting action points
      </div>

      {/* Current AP section */}
      <div className="click-popup-separator" />
      <div className="click-popup-modifiers">
        {/* Base AP value */}
        <div className="click-popup-base-row">
          <span>Base AP</span>
          <span className="stat-value">{apBreakdown.base}</span>
        </div>

        {/* AP Modifiers */}
        {apBreakdown.modifiers.length > 0 && (
          <>
            <div className="click-popup-modifier-separator" />
            <div className="click-popup-modifiers-section">
              {apBreakdown.modifiers.map((mod, index) => {
                const isPositive = mod.value > 0;
                const sign = isPositive ? '+' : '';
                const rowClass = isPositive ? 'positive' : 'negative';

                // Render icon based on source type
                const renderSourceIcon = () => {
                  if (mod.source?.type === 'unit' && mod.source?.id) {
                    const unitId = parseInt(mod.source.id);
                    if (!isNaN(unitId) && unitId > 0) {
                      const sourceUnit = useGameStore.getState().getUnitOrAvailableUnitById(unitId);
                      if (sourceUnit) {
                        return (
                          <span className="modifier-icon" style={{ pointerEvents: 'none' }}>
                            <UnitIcon unitId={sourceUnit.id} size="small" />
                          </span>
                        );
                      }
                    }
                  }
                  return <span className="modifier-icon">{getSourceIcon(mod.source)}</span>;
                };

                return (
                  <div key={`ap-mod-${index}`} className={`click-popup-modifier-row ${rowClass}`}>
                    {renderSourceIcon()}
                    <span className="modifier-name">
                      <EffectIcon
                        effectId={mod.effectId}
                        variant="with-name"
                        className={`effect-modifier ${rowClass}`}
                      />
                    </span>
                    <span className="modifier-value">{sign}{mod.value}</span>
                    <span className={`modifier-duration ${mod.unlimited ? 'unlimited' : ''}`}>
                      {mod.unlimited ? '(∞)' : `(${mod.on_turn_start_counters}t)`}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Current effective value */}
            <div className="click-popup-modifier-separator" />
            <div className="click-popup-total-row">
              <span>Current AP</span>
              <span className="stat-value-final">{apBreakdown.effective}</span>
            </div>
          </>
        )}
      </div>

      {/* Max AP section if modified */}
      {maxApBreakdown && maxApModifierTotal !== 0 && (
        <>
          <div className="click-popup-separator" />
          <div className="click-popup-modifiers">
            <div className="click-popup-base-row">
              <span>Base Max AP</span>
              <span className="stat-value">{maxApBreakdown.base}</span>
            </div>

            <div className="click-popup-modifier-separator" />
            <div className="click-popup-modifiers-section">
              {maxApBreakdown.modifiers.map((mod, index) => {
                const isPositive = mod.value > 0;
                const sign = isPositive ? '+' : '';
                const rowClass = isPositive ? 'positive' : 'negative';

                return (
                  <div key={`max-ap-mod-${index}`} className={`click-popup-modifier-row ${rowClass}`}>
                    <span className="modifier-icon">{getSourceIcon(mod.source)}</span>
                    <span className="modifier-name">
                      <EffectIcon
                        effectId={mod.effectId}
                        variant="with-name"
                        className={`effect-modifier ${rowClass}`}
                      />
                    </span>
                    <span className="modifier-value">{sign}{mod.value}</span>
                    <span className={`modifier-duration ${mod.unlimited ? 'unlimited' : ''}`}>
                      {mod.unlimited ? '(∞)' : `(${mod.on_turn_start_counters}t)`}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="click-popup-modifier-separator" />
            <div className="click-popup-total-row">
              <span>Max AP</span>
              <span className="stat-value-final">{maxApBreakdown.effective}</span>
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <HoverClickPopup
      trigger={apDisplay}
      placement={placement}
    >
      {apPopupContent}
    </HoverClickPopup>
  );
}